import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Step,
  Stepper,
  StepLabel,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Checkbox,
  InputLabel,
  Chip,
  LinearProgress,
  Alert,
  alpha,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

interface SignUpFormData {
  // Step 1: Organization Details
  organizationName: string;
  industry: string;
  companySize: string;
  country: string;
  
  // Step 2: Admin User Details
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  jobTitle: string;
  phoneNumber: string;
  
  // Step 3: Use Case & Preferences
  primaryUseCase: string;
  currentTools: string[];
  teamSize: string;
  projectTypes: string[];
  interestedFeatures: string[];
  hearAboutUs: string;
  
  // Marketing & Terms
  agreedToTerms: boolean;
  subscribeNewsletter: boolean;
  allowMarketing: boolean;
}

const steps = ['Organization Details', 'Admin Account', 'Preferences'];

const SignUp: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<SignUpFormData>({
    // Step 1
    organizationName: '',
    industry: '',
    companySize: '',
    country: '',
    
    // Step 2
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    jobTitle: '',
    phoneNumber: '',
    
    // Step 3
    primaryUseCase: '',
    currentTools: [],
    teamSize: '',
    projectTypes: [],
    interestedFeatures: [],
    hearAboutUs: '',
    
    // Marketing
    agreedToTerms: false,
    subscribeNewsletter: false,
    allowMarketing: false,
  });

  const industries = [
    'Software Development',
    'Technology',
    'Finance',
    'Healthcare',
    'Education',
    'E-commerce',
    'Manufacturing',
    'Consulting',
    'Marketing/Advertising',
    'Other',
  ];

  const companySizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501-1000 employees',
    '1000+ employees',
  ];

  const useCases = [
    'Software Development',
    'IT Project Management',
    'Product Management',
    'Marketing Campaigns',
    'Professional Services',
    'General Project Management',
    'Other',
  ];

  const toolsOptions = [
    'Jira',
    'Trello',
    'Asana',
    'Monday.com',
    'ClickUp',
    'Microsoft Project',
    'Excel/Spreadsheets',
    'None - Starting Fresh',
    'Other',
  ];

  const teamSizeOptions = [
    '1-5 members',
    '6-10 members',
    '11-25 members',
    '26-50 members',
    '51+ members',
  ];

  const projectTypesOptions = [
    'Agile/Scrum',
    'Kanban',
    'Waterfall',
    'Hybrid',
    'SAFe',
    'Multiple Methodologies',
  ];

  const featuresOptions = [
    '🤖 AI-Powered Estimation',
    '📊 Advanced Analytics',
    '⏱️ Time Tracking',
    '📅 Sprint Planning',
    '🔗 Integrations',
    '📈 Reporting & Dashboards',
    '👥 Team Collaboration',
    '📱 Mobile Access',
  ];

  const hearAboutUsOptions = [
    'Search Engine (Google, Bing)',
    'Social Media',
    'Word of Mouth',
    'Industry Blog/Article',
    'Conference/Event',
    'Advertisement',
    'Recommendation',
    'Other',
  ];

  const handleChange = (field: keyof SignUpFormData) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    setError(null);
  };

  const handleMultiSelect = (field: keyof SignUpFormData, value: string) => {
    const currentValues = formData[field] as string[];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    
    setFormData({
      ...formData,
      [field]: newValues,
    });
  };

  const handleCheckboxChange = (field: keyof SignUpFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
    });
  };

  const validateStep = (step: number): boolean => {
    setError(null);

    switch (step) {
      case 0:
        if (!formData.organizationName.trim()) {
          setError('Organization name is required');
          return false;
        }
        if (!formData.industry) {
          setError('Please select an industry');
          return false;
        }
        if (!formData.companySize) {
          setError('Please select company size');
          return false;
        }
        if (!formData.country.trim()) {
          setError('Country is required');
          return false;
        }
        break;

      case 1:
        if (!formData.firstName.trim() || !formData.lastName.trim()) {
          setError('First and last name are required');
          return false;
        }
        if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          setError('Valid email is required');
          return false;
        }
        if (formData.password.length < 8) {
          setError('Password must be at least 8 characters');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        if (!formData.jobTitle.trim()) {
          setError('Job title is required');
          return false;
        }
        break;

      case 2:
        if (!formData.primaryUseCase) {
          setError('Please select your primary use case');
          return false;
        }
        if (!formData.teamSize) {
          setError('Please select your team size');
          return false;
        }
        if (!formData.agreedToTerms) {
          setError('You must agree to the Terms of Service');
          return false;
        }
        break;
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call API to register organization and admin
      const response = await apiService.registerOrganization({
        organization: {
          name: formData.organizationName,
          industry: formData.industry,
          companySize: formData.companySize,
          country: formData.country,
        },
        admin: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          jobTitle: formData.jobTitle,
          phoneNumber: formData.phoneNumber,
        },
        preferences: {
          primaryUseCase: formData.primaryUseCase,
          currentTools: formData.currentTools,
          teamSize: formData.teamSize,
          projectTypes: formData.projectTypes,
          interestedFeatures: formData.interestedFeatures,
          hearAboutUs: formData.hearAboutUs,
          subscribeNewsletter: formData.subscribeNewsletter,
          allowMarketing: formData.allowMarketing,
        },
      });

      setSuccess(true);
      
      // Wait 2 seconds to show success message, then redirect to login
      setTimeout(() => {
        navigate('/auth?registered=true');
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <BusinessIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>
                Tell us about your organization
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We'll use this to customize your experience
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Organization Name"
              value={formData.organizationName}
              onChange={handleChange('organizationName')}
              required
              placeholder="Acme Corporation"
            />

            <FormControl fullWidth required>
              <InputLabel>Industry</InputLabel>
              <Select
                value={formData.industry}
                onChange={handleChange('industry') as any}
                label="Industry"
              >
                {industries.map((industry) => (
                  <MenuItem key={industry} value={industry}>
                    {industry}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Company Size</InputLabel>
              <Select
                value={formData.companySize}
                onChange={handleChange('companySize') as any}
                label="Company Size"
              >
                {companySizes.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Country"
              value={formData.country}
              onChange={handleChange('country')}
              required
              placeholder="United States"
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <PersonIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>
                Create your admin account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You'll have full control over your organization
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                required
              />
            </Stack>

            <TextField
              fullWidth
              label="Work Email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              required
              placeholder="you@company.com"
            />

            <TextField
              fullWidth
              label="Job Title"
              value={formData.jobTitle}
              onChange={handleChange('jobTitle')}
              required
              placeholder="Project Manager, CTO, etc."
            />

            <TextField
              fullWidth
              label="Phone Number (Optional)"
              value={formData.phoneNumber}
              onChange={handleChange('phoneNumber')}
              placeholder="+1 (555) 123-4567"
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleChange('password')}
              required
              helperText="At least 8 characters"
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              required
            />
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <SettingsIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>
                Customize your experience
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Help us tailor TYVRA to your needs
              </Typography>
            </Box>

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                Primary Use Case *
              </FormLabel>
              <RadioGroup
                value={formData.primaryUseCase}
                onChange={handleChange('primaryUseCase')}
              >
                {useCases.map((useCase) => (
                  <FormControlLabel
                    key={useCase}
                    value={useCase}
                    control={<Radio />}
                    label={useCase}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Team Size</InputLabel>
              <Select
                value={formData.teamSize}
                onChange={handleChange('teamSize') as any}
                label="Team Size"
              >
                {teamSizeOptions.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Current Tools (Select all that apply)
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {toolsOptions.map((tool) => (
                  <Chip
                    key={tool}
                    label={tool}
                    onClick={() => handleMultiSelect('currentTools', tool)}
                    color={formData.currentTools.includes(tool) ? 'primary' : 'default'}
                    variant={formData.currentTools.includes(tool) ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Project Methodologies
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {projectTypesOptions.map((type) => (
                  <Chip
                    key={type}
                    label={type}
                    onClick={() => handleMultiSelect('projectTypes', type)}
                    color={formData.projectTypes.includes(type) ? 'primary' : 'default'}
                    variant={formData.projectTypes.includes(type) ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Features You're Most Interested In
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {featuresOptions.map((feature) => (
                  <Chip
                    key={feature}
                    label={feature}
                    onClick={() => handleMultiSelect('interestedFeatures', feature)}
                    color={formData.interestedFeatures.includes(feature) ? 'primary' : 'default'}
                    variant={formData.interestedFeatures.includes(feature) ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            </Box>

            <FormControl fullWidth>
              <InputLabel>How did you hear about us?</InputLabel>
              <Select
                value={formData.hearAboutUs}
                onChange={handleChange('hearAboutUs') as any}
                label="How did you hear about us?"
              >
                {hearAboutUsOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.agreedToTerms}
                    onChange={handleCheckboxChange('agreedToTerms')}
                    required
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <MuiLink href="#" color="primary">
                      Terms of Service
                    </MuiLink>{' '}
                    and{' '}
                    <MuiLink href="#" color="primary">
                      Privacy Policy
                    </MuiLink>{' '}
                    *
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.subscribeNewsletter}
                    onChange={handleCheckboxChange('subscribeNewsletter')}
                  />
                }
                label={
                  <Typography variant="body2">
                    Send me product updates and best practices
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.allowMarketing}
                    onChange={handleCheckboxChange('allowMarketing')}
                  />
                }
                label={
                  <Typography variant="body2">
                    I'd like to receive special offers and promotions
                  </Typography>
                }
              />
            </Box>
          </Stack>
        );

      default:
        return null;
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            maxWidth: 500,
            textAlign: 'center',
            borderRadius: 4,
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: 80,
              color: theme.palette.success.main,
              mb: 2,
            }}
          />
          <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
            Welcome to TYVRA!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your organization has been created successfully. We're redirecting you to login...
          </Typography>
          <LinearProgress />
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: 6,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              fontWeight={900}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              TYVRA
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              Start Your Free 14-Day Trial
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No credit card required
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          {renderStepContent(activeStep)}

          {/* Navigation Buttons */}
          <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              size="large"
              fullWidth
            >
              Back
            </Button>
            <Button
              onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
            >
              {loading ? 'Processing...' : activeStep === steps.length - 1 ? 'Create Account' : 'Next'}
            </Button>
          </Stack>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <MuiLink
                component="button"
                onClick={() => navigate('/auth')}
                sx={{ fontWeight: 600 }}
              >
                Sign In
              </MuiLink>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SignUp;

